import { NSARouterMakerResult } from "namirasoft-account-react";
import { BaseUUID, EncodingOperation } from "namirasoft-core";
import { EncryptionOperation, NSBox, NSButton, NSButtonBlue, NSLabel, NSLoading, NSPanel, NSRow } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { PasswordLayout } from "../PasswordLayout";
import { PasswordRouterProps } from "../PasswordRouterProps";
import { PasswordQuestion } from "../PasswordQuestion";
import PasswordForm from "../components/PasswordForm";
import QuestionsForm from "../components/QuestionsForm";
import QuestionsSummary from "../components/QuestionsSummary";
import Styles from "./SetupWizard.module.css";

export interface SetupWizardProps extends NSARouterMakerResult, PasswordRouterProps
{
    loadUser: () => Promise<void>;
}

export interface SetupWizardState
{
    step: number;
    loading: boolean;
    finished: null | { questions: string[], answers: string[] };
}

class SetupWizard extends Component<SetupWizardProps, SetupWizardState>
{
    private static steps = [
        "Set your Master Password for Namirasoft Password to protect access to your vault.",
        "Please Answer the questions, also you can change the questions",
        "Please Answer the questions, also you can change the questions",
        "Please Answer the questions, also you can change the questions"
    ];
    private PasswordForm_Ref = createRef<PasswordForm>();
    private QuestionsForm_Ref = createRef<QuestionsForm>();
    constructor(props: SetupWizardProps)
    {
        super(props);
        this.state = {
            step: 1,
            loading: false,
            finished: null
        };
        this.prevStep = this.prevStep.bind(this);
        this.nextStep = this.nextStep.bind(this);
    }

    nextStep()
    {
        if (this.state.step < SetupWizard.steps.length)
            this.setState((prev) => ({ step: prev.step + 1 }));
    }

    prevStep()
    {
        if (this.state.step > 1)
            this.setState((prev) => ({ step: prev.step - 1 }));
    }
    async setup(password: string, questions: string[], answers: string[]): Promise<boolean>
    {
        let password_encoded = EncodingOperation.Base64Encode(password);
        let master_key = BaseUUID.uuid(256);
        let master_key_ciphered = await EncryptionOperation.AES256GCMEncrypt(password_encoded, master_key);

        let answers_ciphered = await PasswordQuestion.buildAnswerCiphers(answers, master_key);

        try
        {
            await this.props.servers.password.user.Setup({
                questions: questions,
                cipher: master_key_ciphered,
                ciphers: answers_ciphered,
            });
        }
        catch
        {
            // The server already reported the error through the notifier.
            return false;
        }
        return true;
    }
    override render()
    {
        return (
            <PasswordLayout {...this.props}>
                <NSPanel classList={["mx-auto"]}>
                    {
                        <div style={{ display: !this.state.finished && !this.state.loading ? "contents" : "none" }}>
                            <NSLabel
                                title={`Step ${this.state.step} / ${SetupWizard.steps.length}`}
                                description={SetupWizard.steps[this.state.step - 1]}
                                style={{ width: "100%" }}
                            />
                            <div style={{ display: this.state.step === 1 ? "unset" : "none" }}>
                                <PasswordForm
                                    ref={this.PasswordForm_Ref}
                                    {...this.props}
                                >
                                </PasswordForm>
                            </div>
                            <QuestionsForm
                                ref={this.QuestionsForm_Ref}
                                {...this.props}
                                questions={PasswordQuestion.LIST}
                                visibleGroup={this.state.step - 2}
                            />
                            <NSRow style={{ position: "relative" }}>
                                <NSButtonBlue
                                    title={this.state.step === SetupWizard.steps.length ? "Apply" : "Next"}
                                    onClick={{
                                        action: async (onFinished) =>
                                        {
                                            try
                                            {
                                                if (this.state.step === 1)
                                                {
                                                    this.PasswordForm_Ref.current?.getPasswords();
                                                    this.nextStep();
                                                }
                                                else if (this.state.step === 2)
                                                {
                                                    this.QuestionsForm_Ref.current?.getGroupValue(0, true);
                                                    this.nextStep();
                                                }
                                                else if (this.state.step === 3)
                                                {
                                                    this.QuestionsForm_Ref.current?.getGroupValue(1, true);
                                                    this.nextStep();
                                                }
                                                else if (this.state.step === 4)
                                                {
                                                    this.setState(() => ({ loading: true }));

                                                    let password = this.PasswordForm_Ref.current?.getPasswords()?.password ?? "";
                                                    let { questions, answers } = this.QuestionsForm_Ref.current?.getValue() ?? { questions: [], answers: [] };
                                                    if (await this.setup(password, questions, answers))
                                                        this.setState({ finished: { questions, answers }, loading: false });
                                                    else
                                                        this.setState(() => ({ loading: false }));
                                                }
                                                onFinished();
                                            } catch (error)
                                            {
                                                // Input validation and local encryption errors are not reported by the server, so notify here.
                                                this.props.notifier.onError(error as Error);
                                                this.setState(() => ({ loading: false }));
                                                onFinished();
                                            }
                                        },
                                        showLoading: this.state.step === SetupWizard.steps.length
                                    }}
                                />
                                {
                                    this.state.step > 1 &&
                                    <NSButton
                                        title=""
                                        icon={{ src: "https://static.namirasoft.com/image/concept/arrow/left/white-blue.svg" }}
                                        onClick={{ showLoading: false, action: this.prevStep }}
                                        classList={[Styles.ns_arrow_button]}
                                    />
                                }
                            </NSRow>

                        </div>
                    }

                    {
                        this.state.step === 4 && this.state.loading &&
                        <NSLoading
                            title="Getting ready"
                            description="Sending setup data..."
                            style={{ width: NSBox.width.double }}
                        />
                    }

                    {
                        <div style={{ display: this.state.finished ? "unset" : "none" }}>
                            <QuestionsSummary
                                {...this.props}
                                questions={this.state.finished?.questions ?? []}
                                answers={this.state.finished?.answers ?? []}
                                loadUser={this.props.loadUser}
                            />
                        </div>
                    }
                </NSPanel>
            </PasswordLayout>
        )
    }
}

export default SetupWizard;